import { useState, useEffect } from 'react';

/**
 * Hook to fetch and manage trading instruments
 * Fetches from /api/instruments on mount and provides instrument data
 */
export const useInstruments = (apiBaseUrl = '') => {
  const [instruments, setInstruments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defaultInstrument, setDefaultInstrument] = useState(null);

  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        setLoading(true);
        setError(null);

        const hostname = window.location.hostname;
        const url = `${apiBaseUrl}/api/instruments`;
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.status === 'success' && data.instruments) {
          setInstruments(data.instruments);
          
          // Determine which instrument to show based on domain/subdomain
          const symbols = Object.keys(data.instruments);
          
          // Debug logging
          console.log(`🔍 [useInstruments] Hostname: ${hostname}, Available symbols:`, symbols);
          
          if (symbols.length > 0) {
            let selectedInstrument = null;
            
            // Check if we're on a subdomain (e.g., hori.amberhorizon.eu)
            if (hostname.includes('.') && hostname !== 'amberhorizon.eu' && hostname !== 'www.amberhorizon.eu') {
              // Extract subdomain
              const subdomain = hostname.split('.')[0];
              
              // Try to find instrument matching subdomain
              // Map common subdomain patterns to instrument symbols
              const subdomainMap = {
                'hori': 'HORIZON',
                'horizon': 'HORIZON',
                'amber': 'AMBER',
                'gold': 'GOLD'
              };
              
              const mappedSymbol = subdomainMap[subdomain.toLowerCase()];
              
              if (mappedSymbol && symbols.includes(mappedSymbol)) {
                selectedInstrument = mappedSymbol;
              }
            } else {
              // Root domain (amberhorizon.eu) should ALWAYS show AMBER
              if (hostname === 'amberhorizon.eu' || hostname === 'www.amberhorizon.eu') {
                // Prioritize AMBER on root domain
                if (symbols.includes('AMBER')) {
                  selectedInstrument = 'AMBER';
                } else {
                  // If AMBER not available, use first instrument as fallback
                  selectedInstrument = symbols[0];
                }
              }
            }
            
            // Fallback to first instrument if no domain-based match (only for non-root domains)
            if (!selectedInstrument) {
              selectedInstrument = symbols[0];
            }
            
            console.log(`✅ [useInstruments] Selected instrument: ${selectedInstrument} for hostname: ${hostname}`);
            setDefaultInstrument(selectedInstrument);
          } else {
            throw new Error('No instruments available');
          }
        } else {
          throw new Error('Invalid response format from /api/instruments');
        }

        setLoading(false);
      } catch (err) {
        console.error('❌ Error fetching instruments:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchInstruments();
  }, [apiBaseUrl]);

  /**
   * Get instrument by symbol
   */
  const getInstrument = (symbol) => {
    return instruments[symbol] || null;
  };

  /**
   * Get tick size for a symbol
   */
  const getTickSize = (symbol) => {
    const instrument = getInstrument(symbol);
    return instrument ? instrument.tick_size : null;
  };

  /**
   * Validate if a price is valid for the instrument (divisible by tick_size)
   */
  const validatePrice = (symbol, price) => {
    const tickSize = getTickSize(symbol);
    if (!tickSize) return false;

    // Check if price is a multiple of tick_size
    const remainder = price % tickSize;
    return Math.abs(remainder) < 0.0000001; // Handle floating point precision
  };

  /**
   * Round price to nearest valid tick
   */
  const roundToTick = (symbol, price) => {
    const tickSize = getTickSize(symbol);
    if (!tickSize) return price;

    return Math.round(price / tickSize) * tickSize;
  };

  /**
   * Get all available symbols
   */
  const getSymbols = () => {
    return Object.keys(instruments);
  };

  return {
    instruments,
    loading,
    error,
    defaultInstrument,
    getInstrument,
    getTickSize,
    validatePrice,
    roundToTick,
    getSymbols
  };
};

